
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { CheckCircle, AlertCircle, Loader2, Phone } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { MultiFactorAuth } from "./MultiFactorAuth";
import { VerificationMethod } from "@/hooks/useVerificationMethods";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePhoneVerification } from "@/hooks/usePhoneVerification";
import { toast } from "sonner";
import { PhoneOtpVerification } from "./PhoneOtpVerification";

export const PendingApproval = () => {
  const { user } = useAuth();
  const [showVerification, setShowVerification] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showAddPhone, setShowAddPhone] = useState(false);

  // Use the phone verification hook
  const {
    loading: phoneLoading,
    showVerification: showPhoneOtp,
    initiatePhoneVerification,
    verifyOtp,
    cancelVerification,
    resendCode
  } = usePhoneVerification({
    onVerificationComplete: () => {
      setPhoneVerified(true);
      setShowAddPhone(false);
      toast.success("Phone number verified successfully!");
    }
  });

  useEffect(() => {
    if (user) {
      setEmailVerified(!!user.email_confirmed_at);
      setPhoneVerified(!!user.phone_confirmed_at);
      setLoading(false);
      if (user.phone) {
        setPhoneNumber(user.phone);
      }
    }
  }, [user]);

  const handleAddPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phoneNumber || !phoneNumber.startsWith('+')) {
      toast.error('Phone number must start with country code (e.g., +61)');
      return;
    }
    
    await initiatePhoneVerification(phoneNumber);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show the phone verification form if the user is adding a phone
  if (showAddPhone) {
    if (showPhoneOtp) {
      return (
        <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-2">
              <CardTitle className="text-xl font-bold">Verify Your Phone</CardTitle>
              <CardDescription>
                Enter the verification code sent to your phone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PhoneOtpVerification
                verifyPhoneOtp={verifyOtp}
                resendCode={resendCode}
                loading={phoneLoading}
                onVerified={() => {}}
              />
              <Button 
                variant="outline" 
                onClick={() => {
                  cancelVerification();
                  setShowAddPhone(false);
                }}
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-2">
            <CardTitle className="text-xl font-bold">Add Your Phone Number</CardTitle>
            <CardDescription>
              Add and verify your phone number while your account is being reviewed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddPhone} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    placeholder="+61412345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="flex-1"
                    disabled={phoneLoading}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Include country code (e.g., +61 for Australia)</p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={phoneLoading}
              >
                {phoneLoading ? 'Sending code...' : 'Verify Phone Number'}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => setShowAddPhone(false)}
                disabled={phoneLoading}
                className="w-full mt-2"
              >
                Cancel
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show email verification component if the user clicked to verify
  if (showVerification) {
    const verificationMethods: VerificationMethod[] = [];
    
    if (!emailVerified && user?.email) {
      verificationMethods.push({
        type: 'email',
        value: user.email,
        required: true
      });
    }
    
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-2">
            <CardTitle className="text-xl font-bold">Verify Your Email</CardTitle>
            <CardDescription>
              Complete verification while your account is being reviewed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MultiFactorAuth
              methods={verificationMethods}
              onComplete={() => setShowVerification(false)}
              onCancel={() => setShowVerification(false)}
              requireAllVerified={true}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-xl font-bold">Signup Complete</CardTitle>
          <CardDescription className="text-base">
            Thank you for signing up! Your account has been created and is currently under review by an administrator.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">  
          {(!emailVerified || !phoneVerified) && (
            <Alert variant="warning" className="bg-amber-50 border-amber-200 mt-4">
              <AlertCircle className="h-4 w-4 text-amber-500 mr-2" />
              <AlertDescription className="text-amber-700">
                While you wait, please complete verification of your contact details to ensure you can use all features once approved.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2 mt-4">
            <h3 className="text-sm font-medium">Verification checklist:</h3>
            <div className="flex items-center gap-2">
              {emailVerified ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-amber-500" />
              )}
              <span className={emailVerified ? "text-green-700" : "text-amber-700"}>
                Email verification {emailVerified ? "complete" : "pending"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {phoneVerified ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-amber-500" />
              )}
              <span className={phoneVerified ? "text-green-700" : "text-amber-700"}>
                Phone verification {phoneVerified ? "complete" : "pending"}
              </span>
            </div>
          </div>
          
          <p className="text-center text-muted-foreground mt-4">
            You will receive a notification once your account has been approved. This process typically takes 1-2 business days.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          {!emailVerified && (
            <Button 
              onClick={() => setShowVerification(true)}
              variant="outline"
              className="w-full"
            >
              Verify Email
            </Button>
          )}
          
          {!phoneVerified && (
            <Button 
              onClick={() => setShowAddPhone(true)}
              variant="outline"
              className="w-full"
            >
              {user?.phone ? "Verify Phone Number" : "Add Phone Number"}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};
